import React from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Edit, Mail, Check, X } from 'lucide-react';
import SurveyResponseCard from './SurveyResponseCard';

const STATUS_STYLES = {
  pending: 'bg-slate-100 text-slate-600',
  scheduled: 'bg-blue-100 text-blue-700',
  completed: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-red-100 text-red-700'
};

const StatusBadge = ({ status }) => (
  <Badge className={`text-[10px] rounded-lg ${STATUS_STYLES[status] || STATUS_STYLES.pending}`}>
    {(status || 'pending').charAt(0).toUpperCase() + (status || 'pending').slice(1)}
  </Badge>
);

const MetBadge = ({ met }) => {
  if (met === true) {
    return (
      <span className="flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium bg-emerald-500 text-white">
        <Check className="h-3 w-3" /> Met
      </span>
    );
  }
  if (met === false) {
    return (
      <span className="flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium bg-red-500 text-white">
        <X className="h-3 w-3" /> Not Met
      </span>
    );
  }
  return (
    <span className="px-2.5 py-1 rounded-full text-[11px] font-medium bg-slate-200 text-slate-500">
      Not evaluated
    </span>
  );
};

export const BriefingSessionSummary = ({ session, index, formatDate, onEdit, onNotify }) => (
  <div
    className="rounded-xl p-4 bg-white border border-violet-100 shadow-sm hover:shadow-md transition-all"
    data-testid={`briefing-summary-${index + 1}`}
  >
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-3">
        <div className={`h-8 w-8 rounded-lg flex items-center justify-center shadow-sm ${session.is_extra ? 'bg-gradient-to-br from-amber-500 to-orange-500' : 'bg-gradient-to-br from-violet-500 to-purple-600'}`}>
          <span className="text-white text-xs font-bold">B{index + 1}</span>
        </div>
        <div>
          <h4 className="font-semibold text-sm text-slate-700">Briefing Session {index + 1}</h4>
          <div className="flex items-center gap-1.5 mt-0.5">
            <StatusBadge status={session.status} />
            {session.attendance && session.attendance !== 'pending' && (
              <Badge className={`text-[10px] rounded-lg ${session.attendance === 'present' ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                {session.attendance.charAt(0).toUpperCase() + session.attendance.slice(1)}
              </Badge>
            )}
            {session.is_extra && <Badge className="bg-amber-100 text-amber-700 text-[10px] rounded-lg">Extra</Badge>}
          </div>
        </div>
      </div>
      <div className="flex gap-1">
        <Button
          size="sm"
          variant="ghost"
          className="h-8 w-8 p-0 text-violet-600 hover:bg-violet-100 rounded-lg"
          onClick={onEdit}
          title="Update briefing session"
          data-testid={`edit-briefing-${index + 1}`}
        >
          <Edit className="h-3.5 w-3.5" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="h-8 px-2 text-violet-600 hover:bg-violet-100 rounded-lg"
          onClick={onNotify}
          title="Send schedule email"
          data-testid={`notify-briefing-${index + 1}`}
        >
          <Mail className="h-3.5 w-3.5 mr-1" />
          <span className="text-xs">Notify</span>
        </Button>
      </div>
    </div>

    {session.topics?.length > 0 && (
      <div className="mb-3 space-y-1.5">
        <span className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">Topics to Cover</span>
        <div className="flex flex-wrap gap-1.5">
          {session.topics.map((topic, tIdx) => (
            <span
              key={tIdx}
              className={`text-xs px-2 py-1 rounded-full ${topic.covered ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}
            >
              {topic.covered ? '✓ Covered' : '○ Not Covered'} · {topic.name}
            </span>
          ))}
        </div>
      </div>
    )}

    {session.feedback ? (
      <div>
        <span className="text-[10px] uppercase tracking-wide text-slate-500 font-medium block mb-1">Detailed Feedback</span>
        <p className="text-xs text-slate-600 bg-violet-50/50 p-3 rounded-lg border border-violet-100/50">"{session.feedback}"</p>
      </div>
    ) : (
      <p className="text-xs text-slate-400 italic">No feedback added yet</p>
    )}

    {session.survey_response && (
      <div className="mt-3">
        <SurveyResponseCard surveyResponse={session.survey_response} sessionName={`Briefing Session ${index + 1}`} />
      </div>
    )}

    {session.scheduled_datetime && (
      <div className="mt-3">
        <span className="text-xs font-medium text-violet-700 bg-violet-100 px-2.5 py-1 rounded-lg">
          📅 {formatDate(session.scheduled_datetime)}
        </span>
      </div>
    )}
  </div>
);

export const DemoRoundSummary = ({ round, index, formatDate, onEdit, onNotify }) => (
  <div
    className="rounded-xl p-4 bg-white border border-blue-100 shadow-sm hover:shadow-md transition-all"
    data-testid={`demo-round-summary-${index + 1}`}
  >
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-3">
        <div className={`h-8 w-8 rounded-lg flex items-center justify-center shadow-sm ${round.is_extra ? 'bg-gradient-to-br from-amber-500 to-orange-500' : 'bg-gradient-to-br from-blue-500 to-indigo-600'}`}>
          <span className="text-white text-xs font-bold">H{index + 1}</span>
        </div>
        <div>
          <h4 className="font-semibold text-sm text-slate-700">Demo HR Round {index + 1}</h4>
          <div className="flex items-center gap-1.5 mt-0.5">
            <StatusBadge status={round.status} />
            {round.is_extra && <Badge className="bg-amber-100 text-amber-700 text-[10px] rounded-lg">Extra</Badge>}
          </div>
        </div>
      </div>
      <div className="flex gap-1">
        <Button
          size="sm"
          variant="ghost"
          className="h-8 w-8 p-0 text-blue-600 hover:bg-blue-100 rounded-lg"
          onClick={onEdit}
          title="Update demo HR round"
          data-testid={`edit-demo-round-${index + 1}`}
        >
          <Edit className="h-3.5 w-3.5" />
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className="h-8 px-2 text-blue-600 hover:bg-blue-100 rounded-lg"
          onClick={onNotify}
          title="Send schedule email"
          data-testid={`notify-demo-round-${index + 1}`}
        >
          <Mail className="h-3.5 w-3.5 mr-1" />
          <span className="text-xs">Notify</span>
        </Button>
      </div>
    </div>

    {round.is_extra && round.extra_reason && (
      <p className="text-xs text-amber-700 mb-3 p-2 bg-amber-50 rounded-lg border border-amber-100">
        <strong>Reason:</strong> {round.extra_reason}
      </p>
    )}

    {round.checkpoints?.length > 0 ? (
      <div className="space-y-2">
        <span className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">Assessment Checkpoints</span>
        {round.checkpoints.map((cp, cpIdx) => (
          <div key={cpIdx} className="bg-slate-50 p-2.5 rounded-lg border border-slate-100">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-xs font-medium text-slate-700">{cp.name}</p>
                {cp.description && <p className="text-[11px] text-slate-500">{cp.description}</p>}
              </div>
              <MetBadge met={cp.met === true ? true : cp.met === false ? false : null} />
            </div>
            {cp.notes && <p className="text-[11px] text-slate-600 mt-1.5 italic">{cp.notes}</p>}
          </div>
        ))}
      </div>
    ) : (
      <p className="text-xs text-slate-400 italic">No checkpoints configured for this round</p>
    )}

    {round.feedback && (
      <div className="mt-3">
        <span className="text-[10px] uppercase tracking-wide text-slate-500 font-medium block mb-1">Detailed Feedback</span>
        <p className="text-xs text-slate-600 bg-blue-50/50 p-3 rounded-lg border border-blue-100/50">"{round.feedback}"</p>
      </div>
    )}

    {round.survey_response && (
      <div className="mt-3">
        <SurveyResponseCard surveyResponse={round.survey_response} sessionName={`Demo HR Round ${index + 1}`} />
      </div>
    )}

    {round.scheduled_datetime && (
      <div className="mt-3">
        <span className="text-xs font-medium text-blue-700 bg-blue-100 px-2.5 py-1 rounded-lg">
          📅 {formatDate(round.scheduled_datetime)}
        </span>
      </div>
    )}
  </div>
);
